import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  MapPin, Phone, Clock, Mountain, Star, ChevronRight, Menu, X,
  MessageCircle, Calendar, Shield, Award, Sunrise, TreePine, Camera
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PACKAGES_DATA, ITINERARIES } from "@/data/packages";

const COMPANY = {
  name: "Dream Paradise Tour and Travels",
  phone: "077808 68639",
  whatsapp: "+919469210343",
  address: "Ray's complex, 3, Baba Demb, Chinar Bagh, Srinagar, Jammu and Kashmir 190003",
};

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1662887957904-201296e951cb?q=80&w=2070&auto=format&fit=crop",
  about: "https://images.unsplash.com/photo-1595815771614-ade9d652a65d?q=80&w=2070&auto=format&fit=crop",
  gallery: [
    "https://images.unsplash.com/photo-1564329494258-3f72215ba175?q=80&w=2070&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1707381076957-ec19b90e9cbe?q=80&w=2070&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1666545386065-e5aabdd92857?q=80&w=2070&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1564327287902-0ccf559d839e?q=80&w=2070&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1666513305976-0531bc3d415f?q=80&w=2070&auto=format&fit=crop",
  ],
};

const IconMap = { sunrise: Sunrise, mountain: Mountain, tree: TreePine, camera: Camera };

const TESTIMONIALS = [
  { id: 1, name: "Rajesh & Priya", location: "Mumbai", rating: 5, text: "Our honeymoon was absolutely magical! The houseboat stay on Dal Lake was a dream come true.", avatar: "R" },
  { id: 2, name: "Amit Sharma", location: "Delhi", rating: 5, text: "The adventure trek to Gulmarg was the highlight of my year. Professional guides and breathtaking views.", avatar: "A" },
  { id: 3, name: "The Mehta Family", location: "Bangalore", rating: 5, text: "Dream Paradise handled everything perfectly. The kids loved the Shikara ride!", avatar: "M" },
];

const fadeInUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const staggerContainer = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.15 } } };

const WhatsAppButton = () => (
  <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\+/g, "")}?text=Hi! I'm interested in Kashmir tour packages.`} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-floating-btn" className="fixed bottom-6 right-6 z-50 flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whatsapp-pulse group">
    <MessageCircle className="w-6 h-6" />
    <span className="font-medium hidden sm:inline">Chat with us</span>
  </a>
);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Packages", href: "#packages" },
    { name: "Gallery", href: "#gallery" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${isScrolled ? "glass shadow-lg py-2" : "bg-transparent py-4"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <a href="#home" data-testid="logo" className="flex items-center gap-3">
            <img 
              src={isScrolled 
                ? "https://customer-assets.emergentagent.com/job_2275851b-900a-4c72-8668-755521eb4c4a/artifacts/blwygjtc_dp_logo_white__1_-removebg-preview.png"
                : "https://customer-assets.emergentagent.com/job_2275851b-900a-4c72-8668-755521eb4c4a/artifacts/n7ptqxpr_dp_logo_white__2_-removebg-preview.png"
              }
              alt="Dream Paradise Logo" 
              className={`h-16 md:h-20 w-auto transition-all duration-300 ${isScrolled ? 'brightness-0' : ''}`}
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} data-testid={`nav-${link.name.toLowerCase()}`} className={`nav-link font-medium ${isScrolled ? "text-slate-700 hover:text-emerald-900" : "text-white/90 hover:text-white"}`}>{link.name}</a>
            ))}
            <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} data-testid="nav-call-btn">
              <Button className="bg-gold hover:bg-gold-dark text-white rounded-full px-6 btn-shine"><Phone className="w-4 h-4 mr-2" />Call Now</Button>
            </a>
          </div>
          <button data-testid="mobile-menu-toggle" className="md:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} /> : <Menu className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />}
          </button>
        </div>
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="md:hidden mt-4 pb-4">
              <div className="flex flex-col gap-4 glass rounded-2xl p-4">
                {navLinks.map((link) => (<a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="text-slate-800 font-medium py-2">{link.name}</a>))}
                <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}><Button className="w-full bg-gold text-white rounded-full"><Phone className="w-4 h-4 mr-2" />Call Now</Button></a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

const HeroSection = () => (
  <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center bg-fixed" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
    <div className="absolute inset-0 hero-overlay" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-6">
        <motion.p variants={fadeInUp} className="font-accent text-gold text-2xl md:text-3xl">Welcome to Kashmir</motion.p>
        <motion.h1 variants={fadeInUp} className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight">Experience<span className="block text-gold-light">Paradise on Earth</span></motion.h1>
        <motion.p variants={fadeInUp} className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">Discover the breathtaking beauty of Kashmir with <span className="font-semibold">{COMPANY.name}</span>. Your journey to heaven starts here.</motion.p>
        <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4 pt-4">
          <a href="#packages" data-testid="hero-explore-btn"><Button className="bg-emerald-900 hover:bg-emerald-800 text-white px-8 py-6 text-lg rounded-full shadow-lg btn-shine">Explore Packages<ChevronRight className="w-5 h-5 ml-2" /></Button></a>
          <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\+/g, "")}?text=Hi! I want to plan my Kashmir trip.`} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"><Button variant="outline" className="bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white/20 px-8 py-6 text-lg rounded-full"><MessageCircle className="w-5 h-5 mr-2" />WhatsApp Us</Button></a>
        </motion.div>
        <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-6 pt-8">
          <div className="flex items-center gap-2 text-white/80"><Shield className="w-5 h-5 text-gold" /><span className="text-sm font-medium">100% Safe Travel</span></div>
          <div className="flex items-center gap-2 text-white/80"><Award className="w-5 h-5 text-gold" /><span className="text-sm font-medium">Best Prices Guaranteed</span></div>
          <div className="flex items-center gap-2 text-white/80"><Clock className="w-5 h-5 text-gold" /><span className="text-sm font-medium">24/7 Support</span></div>
        </motion.div>
      </motion.div>
    </div>
    <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
      <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center"><div className="w-1.5 h-3 bg-white/70 rounded-full mt-2" /></div>
    </motion.div>
  </section>
);

const AboutSection = () => (
  <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <img src={IMAGES.about} alt="Beautiful Kashmir Valley" className="w-full h-[500px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/30 to-transparent" />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6 max-w-[200px]">
            <div className="text-4xl font-heading font-bold text-emerald-900">15+</div>
            <div className="text-slate-600 text-sm">Years of Excellence in Tourism</div>
          </motion.div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
          <p className="text-emerald-700 font-semibold tracking-widest uppercase text-sm">About Us</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 leading-tight">Your Gateway to the <span className="text-emerald-900">Heart of Kashmir</span></h2>
          <p className="text-lg text-slate-600 leading-relaxed">At <span className="font-semibold text-slate-800">{COMPANY.name}</span>, we do not just organize tours – we craft unforgettable experiences. As a locally-owned agency based in the heart of Srinagar, we bring you authentic Kashmir with genuine warmth.</p>
          <p className="text-slate-600 leading-relaxed">From the serene Dal Lake to the snow-capped peaks of Gulmarg, from the lush meadows of Pahalgam to the historical Mughal Gardens – we ensure every moment is magical.</p>
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100"><div className="text-2xl font-heading font-bold text-emerald-900">10,000+</div><div className="text-sm text-slate-500">Happy Travelers</div></div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100"><div className="text-2xl font-heading font-bold text-emerald-900">50+</div><div className="text-sm text-slate-500">Tour Packages</div></div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100"><div className="text-2xl font-heading font-bold text-emerald-900">25+</div><div className="text-sm text-slate-500">Expert Guides</div></div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100"><div className="text-2xl font-heading font-bold text-emerald-900">15+</div><div className="text-sm text-slate-500">Years Experience</div></div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const PackageModal = ({ pkg, isOpen, onClose }) => {
  if (!pkg) return null;
  const itinerary = ITINERARIES[pkg.id] || [];
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden">
        <div className="relative h-48 overflow-hidden">
          <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
          <div className="absolute bottom-4 left-6 right-6">
            <Badge className="bg-gold text-white border-0 mb-2">{pkg.duration}</Badge>
            <DialogTitle className="text-2xl font-heading font-bold text-white">{pkg.name}</DialogTitle>
            <DialogDescription className="text-white/80">{pkg.tagline} - Complete Day-by-Day Itinerary</DialogDescription>
          </div>
        </div>
        <ScrollArea className="h-[50vh] px-6 py-4">
          <div className="space-y-6">
            {itinerary.map((day, index) => (
              <div key={index} className="relative pl-8 pb-6 border-l-2 border-emerald-200 last:pb-0">
                <div className="absolute -left-3 top-0 w-6 h-6 rounded-full bg-emerald-900 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{day.day}</span>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-emerald-700 font-semibold text-sm">Day {day.day}</span>
                    <h4 className="font-heading text-lg font-bold text-slate-900">{day.title}</h4>
                  </div>
                  <ul className="space-y-2">
                    {day.activities.split(" • ").map((activity, actIndex) => (
                      <li key={actIndex} className="flex items-start gap-2 text-slate-600 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0" />
                        <span>{activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="p-6 bg-slate-50 border-t">
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\+/g, "")}?text=Hi! I'm interested in the ${pkg.name} (${pkg.duration}) package. Please share the details and pricing.`} target="_blank" rel="noopener noreferrer" data-testid={`modal-book-btn-${pkg.id}`} className="flex-1">
              <Button className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full py-6 btn-shine"><MessageCircle className="w-5 h-5 mr-2" />Book This Package</Button>
            </a>
            <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} data-testid={`modal-call-btn-${pkg.id}`} className="flex-1">
              <Button variant="outline" className="w-full border-2 border-emerald-900 text-emerald-900 hover:bg-emerald-50 rounded-full py-6"><Phone className="w-5 h-5 mr-2" />Call for Details</Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PackagesSection = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewItinerary = (pkg) => { setSelectedPackage(pkg); setIsModalOpen(true); };

  return (
    <section id="packages" data-testid="packages-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <p className="text-emerald-700 font-semibold tracking-widest uppercase text-sm mb-3">Our Packages</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">Choose Your <span className="text-gold">Adventure</span></h2>
          <p className="text-slate-600 max-w-2xl mx-auto">Handcrafted tour packages designed to give you the best Kashmir experience. Click on any package to view the detailed day-by-day itinerary.</p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PACKAGES_DATA.map((pkg) => {
            const IconComponent = IconMap[pkg.iconType] || Mountain;
            const highlightsArr = pkg.highlights.split(", ");
            return (
              <motion.div key={pkg.id} variants={fadeInUp}>
                <Card data-testid={`package-card-${pkg.id}`} className="package-card h-full bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] border border-slate-100 cursor-pointer" onClick={() => handleViewItinerary(pkg)}>
                  <div className="relative h-48 overflow-hidden">
                    <img src={pkg.image} alt={pkg.name} className="package-image w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                    <Badge className="absolute top-4 right-4 bg-gold text-white border-0 px-3 py-1">{pkg.duration}</Badge>
                    <div className="absolute bottom-4 left-4 text-white"><IconComponent className="w-8 h-8 mb-2" /></div>
                  </div>
                  <CardContent className="p-5 space-y-3">
                    <div><p className="text-gold font-medium text-sm">{pkg.tagline}</p><h3 className="font-heading text-xl font-bold text-slate-900">{pkg.name}</h3></div>
                    <ul className="space-y-1.5">
                      {highlightsArr.slice(0, 3).map((highlight, idx) => (<li key={idx} className="flex items-center gap-2 text-slate-600 text-sm"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{highlight}</li>))}
                      <li className="text-emerald-700 text-sm font-medium">+{pkg.days} days of adventure...</li>
                    </ul>
                    <Separator className="my-3" />
                    <Button data-testid={`package-view-btn-${pkg.id}`} className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full btn-shine" onClick={(e) => { e.stopPropagation(); handleViewItinerary(pkg); }}>View Itinerary<ChevronRight className="w-4 h-4 ml-1" /></Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mt-12">
          <p className="text-slate-600 mb-4">Looking for a custom package?</p>
          <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\+/g, "")}?text=Hi! I'd like to discuss a custom tour package for Kashmir.`} target="_blank" rel="noopener noreferrer" data-testid="custom-package-btn">
            <Button variant="outline" className="border-2 border-emerald-900 text-emerald-900 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg"><Calendar className="w-5 h-5 mr-2" />Plan Custom Trip</Button>
          </a>
        </motion.div>
      </div>
      <PackageModal pkg={selectedPackage} isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedPackage(null); }} />
    </section>
  );
};

const GallerySection = () => (
  <section id="gallery" data-testid="gallery-section" className="py-20 md:py-32 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
        <p className="text-emerald-700 font-semibold tracking-widest uppercase text-sm mb-3">Gallery</p>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">Glimpses of <span className="text-emerald-900">Paradise</span></h2>
        <p className="text-slate-600 max-w-2xl mx-auto">Let these breathtaking views inspire your next adventure in Kashmir</p>
      </motion.div>
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <motion.div variants={fadeInUp} className="col-span-2 row-span-2"><div className="gallery-item rounded-3xl overflow-hidden h-full"><img src={IMAGES.gallery[0]} alt="Dal Lake Shikara" className="w-full h-full object-cover min-h-[400px]" /></div></motion.div>
        <motion.div variants={fadeInUp}><div className="gallery-item rounded-2xl overflow-hidden"><img src={IMAGES.gallery[1]} alt="Kashmir Shikaras" className="w-full h-48 object-cover" /></div></motion.div>
        <motion.div variants={fadeInUp}><div className="gallery-item rounded-2xl overflow-hidden"><img src={IMAGES.gallery[2]} alt="Kashmir Valley River" className="w-full h-48 object-cover" /></div></motion.div>
        <motion.div variants={fadeInUp}><div className="gallery-item rounded-2xl overflow-hidden"><img src={IMAGES.gallery[3]} alt="Dal Lake Floating Market" className="w-full h-48 object-cover" /></div></motion.div>
        <motion.div variants={fadeInUp}><div className="gallery-item rounded-2xl overflow-hidden"><img src={IMAGES.gallery[4]} alt="Kashmir Mountains" className="w-full h-48 object-cover" /></div></motion.div>
      </motion.div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section data-testid="testimonials-section" className="py-20 md:py-32 bg-emerald-900 relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
        <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-3">Testimonials</p>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">What Our Travelers Say</h2>
      </motion.div>
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8">
        {TESTIMONIALS.map((testimonial) => (
          <motion.div key={testimonial.id} variants={fadeInUp}>
            <Card data-testid={`testimonial-card-${testimonial.id}`} className="testimonial-card bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl h-full">
              <CardContent className="p-6 space-y-4">
                <div className="flex gap-1">{[1,2,3,4,5].map((i) => (<Star key={i} className="w-5 h-5 fill-gold text-gold" />))}</div>
                <p className="text-white/90 leading-relaxed italic">&ldquo;{testimonial.text}&rdquo;</p>
                <div className="flex items-center gap-3 pt-4">
                  <div className="w-12 h-12 rounded-full bg-gold flex items-center justify-center text-white font-bold text-lg">{testimonial.avatar}</div>
                  <div><p className="font-semibold text-white">{testimonial.name}</p><p className="text-sm text-white/60">{testimonial.location}</p></div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

const ContactSection = () => (
  <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-8">
          <div>
            <p className="text-emerald-700 font-semibold tracking-widest uppercase text-sm mb-3">Get in Touch</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">Ready to <span className="text-gold">Explore Kashmir?</span></h2>
            <p className="text-slate-600 leading-relaxed">We are here to help you plan the perfect Kashmir experience. Reach out to us anytime!</p>
          </div>
          <div className="space-y-4">
            <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors group">
              <div className="w-14 h-14 bg-emerald-900 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"><Phone className="w-6 h-6 text-white" /></div>
              <div><p className="text-sm text-slate-500">Call Us</p><p className="font-semibold text-slate-900 text-lg">{COMPANY.phone}</p></div>
            </a>
            <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\+/g, "")}?text=Hi! I want to plan my Kashmir trip.`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors group">
              <div className="w-14 h-14 bg-[#25D366] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"><MessageCircle className="w-6 h-6 text-white" /></div>
              <div><p className="text-sm text-slate-500">WhatsApp</p><p className="font-semibold text-slate-900 text-lg">{COMPANY.whatsapp}</p></div>
            </a>
            <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-2xl">
              <div className="w-14 h-14 bg-gold rounded-xl flex items-center justify-center flex-shrink-0"><MapPin className="w-6 h-6 text-white" /></div>
              <div><p className="text-sm text-slate-500">Visit Us</p><p className="font-semibold text-slate-900">{COMPANY.address}</p></div>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl">
            <iframe data-testid="google-map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3304.055738837044!2d74.80048831521086!3d34.08367578059667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38e1856b2c5c7c8d%3A0x8c3c6b6b6b6b6b6b!2sChinar%20Bagh%2C%20Srinagar%2C%20Jammu%20and%20Kashmir%20190003!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin" width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy" className="rounded-3xl" title="Office Location" />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="absolute -bottom-6 left-6 right-6">
            <a href={`https://wa.me/${COMPANY.whatsapp.replace(/\+/g, "")}?text=Hi! I want to book a Kashmir tour.`} target="_blank" rel="noopener noreferrer" data-testid="contact-cta-btn">
              <Button className="w-full bg-emerald-900 hover:bg-emerald-800 text-white py-6 rounded-2xl text-lg shadow-xl btn-shine"><MessageCircle className="w-5 h-5 mr-2" />Start Planning Your Trip</Button>
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid="footer" className="bg-slate-900 text-white py-12">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-8 items-center">
        <div><h3 className="font-heading text-2xl font-bold text-white mb-2">Dream Paradise</h3><p className="text-slate-400 text-sm">Your trusted partner for unforgettable Kashmir experiences</p></div>
        <div className="flex justify-center gap-6">
          <a href="#home" className="text-slate-400 hover:text-white text-sm">Home</a>
          <a href="#about" className="text-slate-400 hover:text-white text-sm">About</a>
          <a href="#packages" className="text-slate-400 hover:text-white text-sm">Packages</a>
          <a href="#gallery" className="text-slate-400 hover:text-white text-sm">Gallery</a>
          <a href="#contact" className="text-slate-400 hover:text-white text-sm">Contact</a>
        </div>
        <div className="text-right"><p className="text-slate-400 text-sm">© {new Date().getFullYear()} {COMPANY.name}</p><p className="text-slate-500 text-xs mt-1">All rights reserved</p></div>
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <div className="App font-body">
      <Navbar />
      <HeroSection />
      <AboutSection />
      <PackagesSection />
      <GallerySection />
      <TestimonialsSection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default App;
